import { AlertCircle, CheckCircle } from 'lucide-react';
import { Project } from '../types/project';
import { TraditionalCalcResults } from '../lib/calc/types';
import { getEditionPack } from '../lib/calc/editions';
import { zeroRatedRows } from '../lib/calc/comparison';
import { kvaToAmps } from '../lib/calc/amps';
import { qualificationStatement } from '../lib/calc/qualification';
import type { ProfileIssue } from '../lib/profileQuality';
import { Tooltip } from './Tooltip';

// The traditional (NEC 220.83 / 120.83) result surface on the Results tab —
// the on-screen mirror of the report's worksheet. Layout and visual language
// follow the measured summary so switching bases doesn't feel like switching
// products.

interface TraditionalResultsSummaryProps {
  results: TraditionalCalcResults;
  project: Partial<Project>;
  /** Profile sanity checks (lib/profileQuality.ts), computed by the caller
   * from the full equipment list. Shown above the determination so a short
   * inventory is questioned where the number is read. */
  profileIssues?: ProfileIssue[];
}

const kva = (va: number) => (va / 1000).toFixed(2);

export function TraditionalResultsSummary({ results, project, profileIssues = [] }: TraditionalResultsSummaryProps) {
  const isPassing = results.adequacyResult === 'PASS';
  const pack = getEditionPack(results.codeEdition);
  const c = results.categories2026;
  const phase = project.service_phase === 'three' ? 'three' : 'single';
  const volts = project.service_voltage_numeric ?? 240;
  const amps = (v: number) => kvaToAmps(v, volts, phase).toFixed(1);
  // Always undefined now that no UI sets a demand-parameter override: the
  // engine keeps the capability and these fall back to the section's values.
  // See the removal note in docs/specs/load-calc-program.md §7.
  const sp = results.statedParameters;

  // Each row carries a stable id (`trad-row-<id>`) so the guided demo and the
  // browser tests can point at one line of the arithmetic.
  const summaryRows: Array<{ id: string; label: string; value: string; cls?: string; tip?: string }> = [];
  if (results.excludedNoncoincidentVA > 0) {
    summaryRows.push({
      id: 'noncoincident',
      label: 'Noncoincident load excluded',
      value: `${kva(results.excludedNoncoincidentVA)} kVA`,
      tip: 'The smaller of heating vs. cooling — the code counts only the larger connected load.',
    });
  }
  if (c) {
    summaryRows.push(
      { id: 'pool', label: 'Existing load (profile + inventory)', value: `${kva(c.existingPoolVA)} kVA` },
      {
        id: 'tier',
        label: `First ${pack.optional83TierKVA} kVA of existing and new at 100%`,
        value: `${kva(c.tierFromExistingVA + c.tierFromNewVA)} kVA`,
      },
      { id: 'remainder', label: 'Remainder of existing at 40%', value: `${kva(c.existingRemainderDemandVA)} kVA` },
    );
    if (c.newEvseVA > 0) summaryRows.push({ id: 'new-evse', label: 'New EVSE at 80%', value: `${kva(c.newEvseDemandVA)} kVA` });
    if (c.newCentralResistanceHeatVA > 0)
      summaryRows.push({ id: 'new-heat', label: 'New central resistance heat at 80%', value: `${kva(c.newCentralResistanceHeatDemandVA)} kVA` });
    if (c.otherNewVA > 0)
      summaryRows.push({ id: 'other-new', label: 'All other new loads at 50%', value: `${kva(c.otherNewDemandVA)} kVA` });
  } else {
    const tierK = sp?.tierKVA ?? pack.optional83TierKVA;
    const tierP = sp?.tierPercent ?? 100;
    const remP = sp?.remainderPercent ?? 40;
    const stated = sp ? ' (preparer-stated)' : '';
    summaryRows.push(
      { id: 'pool', label: 'General load pool (connected)', value: `${kva(results.poolVA)} kVA` },
      { id: 'tier', label: `First ${tierK} kVA of pool at ${tierP}%${stated}`, value: `${kva(results.tierDemandVA)} kVA` },
      { id: 'remainder', label: `Remainder of pool at ${remP}%${stated}`, value: `${kva(results.poolDemandVA - results.tierDemandVA)} kVA` },
    );
    if (results.hvacBucketVA > 0)
      summaryRows.push({ id: 'hvac', label: 'Air-conditioning / space heating at 100%', value: `${kva(results.hvacBucketVA)} kVA` });
    if (results.evseFullVA > 0)
      summaryRows.push({
        id: 'evse',
        label: `EVSE on a separate line at ${sp?.evsePercent ?? 100}%`,
        value: `${kva(results.evseFullDemandVA)} kVA`,
      });
  }

  const zeros = zeroRatedRows(results.lineItems);
  const qualification = qualificationStatement(project);

  return (
    <div data-testid="traditional-results">
      {profileIssues.length > 0 && (
        <div
          data-testid="profile-checks"
          className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900"
        >
          <p className="font-semibold">
            {profileIssues.length} profile check{profileIssues.length > 1 ? 's' : ''} to confirm before relying on this number
          </p>
          <ul className="mt-1 space-y-1 list-disc pl-5">
            {profileIssues.map((issue) => (
              <li key={issue.id + issue.title}>
                <span className="font-medium">{issue.title}.</span> {issue.message}
              </li>
            ))}
          </ul>
        </div>
      )}
      {zeros.length > 0 && (
        <div
          data-testid="zero-rated-banner"
          className="mb-6 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4"
        >
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <p className="font-semibold">
              {zeros.length} item{zeros.length > 1 ? 's' : ''} counted at 0 VA — this result is
              understated.
            </p>
            <p className="mt-1 leading-relaxed">
              {zeros.map((li) => li.label).join(', ')}: no nameplate rating entered. Checked
              heating/cooling items ship without a standard value on purpose — enter the
              nameplate on the Existing Load tab or the calculated load is missing those loads
              entirely.
            </p>
          </div>
        </div>
      )}
      <div
        className={`p-6 rounded-lg border-2 mb-6 ${
          isPassing ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500'
        }`}
      >
        <div className="flex items-center gap-3">
          {isPassing ? (
            <CheckCircle className="text-green-600 flex-shrink-0" size={32} />
          ) : (
            <AlertCircle className="text-red-600 flex-shrink-0" size={32} />
          )}
          <div>
            <h3
              data-testid="result-pass-fail"
              className={`text-2xl font-bold ${isPassing ? 'text-green-900' : 'text-red-900'}`}
            >
              Service Adequacy: {results.adequacyResult}
            </h3>
            <p className={`text-sm mt-1 ${isPassing ? 'text-green-800' : 'text-red-800'}`}>
              {isPassing
                ? `The existing service has adequate capacity for the proposed new loads under NEC ${results.sectionCitation}.`
                : `The existing service does NOT have adequate capacity for the proposed new loads under NEC ${results.sectionCitation}.`}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h4 className="font-semibold text-gray-900 mb-3">Calculation Basis</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Method:</span>
              <span data-testid="trad-citation" className="font-medium">NEC {results.sectionCitation}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Code edition:</span>
              <span className="font-medium">NEC {results.codeEdition}</span>
            </div>
            {qualification && (
              <div className="flex justify-between gap-4">
                <span className="text-gray-600 flex-shrink-0">Scope:</span>
                <span className="font-medium text-right" data-testid="trad-qualification">{qualification}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-600">Floor area:</span>
              <span className="font-medium">{project.dwelling_floor_area_sqft ?? 0} ft²</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Connected load (counted):</span>
              <span className="font-medium">{kva(results.totalConnectedVA)} kVA</span>
            </div>
          </div>
        </div>

        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h4 className="font-semibold text-gray-900 mb-3">Service Capacity</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Service Rating:</span>
              <span className="font-medium">
                {project.service_rating_amps} A @ {project.service_voltage_numeric}V
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Service Capacity:</span>
              <span data-testid="result-service-capacity" className="font-medium">
                {results.serviceCapacityKVA.toFixed(2)} kVA
              </span>
            </div>
            <div className="flex justify-between">
              <span className="inline-flex items-center text-gray-600">
                Utilization: <Tooltip text="Percentage of total service capacity consumed by the calculated demand." />
              </span>
              <span data-testid="result-utilization" className="font-medium">
                {results.utilizationPercent.toFixed(1)}%
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-gray-200 pt-6">
        <h4 className="font-semibold text-gray-900 mb-4 text-sm sm:text-base">
          NEC {results.sectionCitation} Load Calculation Summary
        </h4>
        <div className="overflow-x-auto -mx-6 sm:mx-0">
          <table className="w-full text-xs sm:text-sm min-w-[400px]">
            <tbody className="divide-y divide-gray-200">
              {summaryRows.map((row) => (
                <tr key={row.id} data-testid={`trad-row-${row.id}`}>
                  <td className="py-2 px-2 sm:px-0 text-gray-600">
                    {row.label}
                    {row.tip && <Tooltip text={row.tip} />}
                  </td>
                  <td className="py-2 px-2 sm:px-0 text-right font-medium whitespace-nowrap">{row.value}</td>
                </tr>
              ))}
              <tr className="bg-blue-50 font-semibold">
                <td className="py-2 px-2 sm:px-0 text-gray-900">
                  Calculated Demand
                  <Tooltip text="The total load the service must support under the traditional method, after the code's demand factors." />
                </td>
                <td data-testid="result-adjusted-demand" className="py-2 px-2 sm:px-0 text-right whitespace-nowrap">
                  {results.totalDemandKVA.toFixed(2)} kVA · {amps(results.totalDemandKVA)} A
                </td>
              </tr>
              <tr className="bg-gray-50 font-semibold">
                <td className="py-2 px-2 sm:px-0 text-gray-900">Service Capacity</td>
                <td className="py-2 px-2 sm:px-0 text-right whitespace-nowrap">
                  {results.serviceCapacityKVA.toFixed(2)} kVA
                </td>
              </tr>
              <tr className={`font-bold ${isPassing ? 'bg-green-100' : 'bg-red-100'}`}>
                <td className="py-2 px-2 sm:px-0 text-gray-900">Available Margin</td>
                <td
                  data-testid="result-margin"
                  className={`py-2 px-2 sm:px-0 text-right whitespace-nowrap ${
                    isPassing ? 'text-green-700' : 'text-red-700'
                  }`}
                >
                  {results.availableMarginKVA.toFixed(2)} kVA · {amps(results.availableMarginKVA)} A
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>


      <div className="mt-6 border-t border-gray-200 pt-6">
        <h4 className="font-semibold text-gray-900 mb-4 text-sm sm:text-base">Load Worksheet</h4>
        <div className="overflow-x-auto -mx-6 sm:mx-0">
          <table className="w-full text-xs sm:text-sm min-w-[500px]">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left p-2 font-semibold text-gray-700">Load</th>
                <th className="text-left p-2 font-semibold text-gray-700">Status</th>
                <th className="text-right p-2 font-semibold text-gray-700 whitespace-nowrap">Connected (VA)</th>
                <th className="text-left p-2 font-semibold text-gray-700">Treatment</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {results.lineItems.map((li) => (
                <tr key={li.id}>
                  <td className="p-2 text-gray-900 break-words">{li.label}</td>
                  <td className="p-2">
                    <span
                      className={`inline-block px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${
                        li.origin === 'proposed'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {li.origin === 'proposed' ? 'New' : li.origin === 'existing' ? 'Existing' : 'Dwelling'}
                    </span>
                  </td>
                  <td className="p-2 text-right font-medium whitespace-nowrap">
                    {li.connectedVA.toFixed(0)}
                  </td>
                  <td className="p-2 text-gray-600">{li.treatment}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {(results.positionsApplied.length > 0 || results.warnings.length > 0) && (
        <div className="mt-6 p-4 bg-gray-50 border border-gray-300 rounded-md space-y-2">
          {results.positionsApplied.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-gray-800">Methodology positions applied</p>
              <ul className="mt-1 space-y-1">
                {results.positionsApplied.map((p) => (
                  <li key={p} className="text-xs text-gray-600">{p}</li>
                ))}
              </ul>
            </div>
          )}
          {results.warnings.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-gray-800">Calculation notes</p>
              <ul className="mt-1 space-y-1">
                {results.warnings.map((w) => (
                  <li key={w} className="text-xs text-amber-700">{w}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
